/**
 * PastLivesFlow — state-machine orchestrator for the Past Lives Pack SKU.
 *
 * Mirrors the AvatarFlow shape (consent → upload → invoice → paying → loading
 * → reveal-entrance → carousel) and adds a single extra step: the soft 18+
 * age-gate that fires between upload and invoice, sourced from the user's
 * stored birthDate (see lib/age.ts):
 *
 *   age >= 18         → skip age-gate, go straight to invoice
 *   age <  18         → soft warning modal ("mature historical themes…")
 *   birthDate missing → hard block, redirect user to /me to complete profile
 *
 * Selfie persistence reuses the avatarFlow.r2Key.<code> sessionStorage slot
 * (same selfie may serve multiple SKUs of the same person).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import WebApp from '@twa-dev/sdk';
import { useSku } from '../../hooks/useSku';
import { useSkuInvoice } from '../../hooks/useSkuInvoice';
import { useMe } from '../../hooks/useMe';
import { usePastLivesRun } from '../../hooks/usePastLivesRun';
import { useChart } from '../../hooks/useChart';
import { useAuth } from '../../store/auth';
import { discoverSkuRunIds, getBaselineSkuRunId } from '../../lib/discover-sku-run';
import { computeAgeYears } from '../../lib/age';
import { SelfieConsentModal, hasCurrentBiometricConsent } from '../avatarFlow/SelfieConsentModal';
import { SelfieUploadScreen } from '../avatarFlow/SelfieUploadScreen';
import { PaymentSheet } from '../payment/PaymentSheet';
import { AgeGateModal } from './AgeGateModal';
import { FreeRevealStep } from './FreeRevealStep';
import { UpsellStep } from './UpsellStep';
import { PastLivesLoadingScreen } from './PastLivesLoadingScreen';
import { PastLivesRevealEntrance } from './PastLivesRevealEntrance';
import { PastLivesCarousel } from './PastLivesCarousel';
import { PastLivesGlyph } from './PastLivesGlyph';
import { resolveSkuLabel } from '../../lib/sku-i18n';
import type { PastLivesFlowState } from '../../types/pastLivesFlow';

const r2KeyStorageKey = (code: string): string => `avatarFlow.r2Key.${code}`;

function readPersistedR2Key(code: string): string | null {
  try {
    return sessionStorage.getItem(r2KeyStorageKey(code));
  } catch {
    return null;
  }
}

function clearPersistedR2Key(code: string): void {
  try {
    sessionStorage.removeItem(r2KeyStorageKey(code));
  } catch {
    /* storage unavailable */
  }
}

function wireBackButton(handler: () => void): () => void {
  try {
    const bb = WebApp?.BackButton;
    if (!bb || typeof bb.onClick !== 'function') return () => {};
    bb.onClick(handler);
    bb.show();
    return () => {
      try {
        bb.offClick(handler);
        bb.hide();
      } catch {
        /* noop */
      }
    };
  } catch {
    return () => {};
  }
}

export function PastLivesFlow(): JSX.Element {
  const { code = 'past_lives_pack' } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { sku, isLoading, isError } = useSku(code);
  const { data: me, isLoading: meLoading } = useMe();
  const { purchase, redeemCoupon } = useSkuInvoice<{ selfieR2Key: string }>();
  const jwt = useAuth((s) => s.jwt);

  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const { snapshot, error: runError } = usePastLivesRun(activeRunId);

  // Chart hook for freemium ladder: polls chart status to detect when ready.
  const { data: chartData } = useChart();

  const paidStars = sku?.priceStars ?? undefined;
  const totalCards = 13; // platinum tier baseline (1 hero + 12 grids)

  // Async-aware initial state:
  // - Freemium ON: start at 'chart-calc' (no selfie needed yet).
  // - Freemium OFF: start at 'consent' sentinel while me loads; effect below settles.
  const [state, setState] = useState<PastLivesFlowState>(() => {
    // ALWAYS start at a neutral sentinel — the settle effect below picks the real
    // entry state once /me loads (it carries the runtime freemium flag). We do NOT
    // read the persisted selfie key here: a freemium user with a stale persisted
    // key would otherwise be dropped straight into the selfie/age-gate. The
    // `isLoading || meLoading` guard in render shows a placeholder until me is
    // ready, so this sentinel never flashes.
    return { name: 'consent' };
  });

  // Settle the entry state once /me is loaded (it carries the runtime freemium flag):
  //   freemium ON  → 'chart-calc' (free taste + upsell, no selfie yet) — takes
  //                  priority over any stale persisted selfie key.
  //   freemium OFF → resume a persisted selfie upload, else 'upload' if biometric
  //                  consent already given, else 'consent'.
  useEffect(() => {
    if (meLoading) return;
    setState(prev => {
      if (prev.name !== 'consent') return prev; // don't override progressed states
      if (me?.pastLivesFreemiumEnabled === true) return { name: 'chart-calc' };
      const persisted = readPersistedR2Key(code);
      if (persisted) return { name: 'age-gate', selfieR2Key: persisted };
      if (hasCurrentBiometricConsent(me)) return { name: 'upload' };
      return { name: 'consent' };
    });
  }, [me, meLoading, code]);

  // chart-calc → free-reveal once the chart is ready.
  useEffect(() => {
    if (state.name !== 'chart-calc') return;
    if (chartData?.status === 'ready') {
      setState({ name: 'free-reveal' });
    }
  }, [state.name, chartData]);

  const stateRef = useRef(state);
  stateRef.current = state;

  const goBack = useCallback(() => {
    const cur = stateRef.current;
    switch (cur.name) {
      case 'carousel':
      case 'reveal-entrance':
        navigate('/catalog');
        break;
      case 'chart-calc':
      case 'free-reveal':
      case 'upsell':
        navigate('/catalog');
        break;
      case 'consent':
      case 'upload':
      case 'age-gate':
      case 'invoice':
      case 'paying':
      case 'loading':
      case 'error':
        clearPersistedR2Key(code);
        navigate('/catalog');
        break;
      default:
        navigate('/catalog');
    }
  }, [code, navigate]);

  useEffect(() => wireBackButton(goBack), [goBack]);

  const handleConsentAccept = useCallback(() => setState({ name: 'upload' }), []);
  const handleConsentDecline = useCallback(() => navigate('/catalog'), [navigate]);

  const handleUploaded = useCallback((r2Key: string) => {
    setState({ name: 'age-gate', selfieR2Key: r2Key });
  }, []);

  // Age-gate decision: as soon as we have the user's birthDate, branch.
  useEffect(() => {
    if (state.name !== 'age-gate') return;
    if (!me) return;
    const age = computeAgeYears(me.birthDate);
    if (age === null) {
      // birthDate missing — modal will show 'hard-missing' until user cancels.
      return;
    }
    if (age >= 18) {
      setState({ name: 'invoice', selfieR2Key: state.selfieR2Key });
    }
    // age < 18 — stay on age-gate (soft mode), wait for explicit confirm/cancel.
  }, [state, me]);

  const ageGateMode = (() => {
    if (state.name !== 'age-gate') return null;
    if (!me) return null;
    const age = computeAgeYears(me.birthDate);
    if (age === null) return 'hard-missing' as const;
    if (age < 18) return 'soft-under-18' as const;
    return null;
  })();

  const handleAgeGateConfirm = useCallback(() => {
    if (state.name !== 'age-gate') return;
    setState({ name: 'invoice', selfieR2Key: state.selfieR2Key });
  }, [state]);

  const handleAgeGateCancel = useCallback(() => {
    clearPersistedR2Key(code);
    if (ageGateMode === 'hard-missing') {
      navigate('/me');
    } else {
      navigate('/catalog');
    }
  }, [code, navigate, ageGateMode]);

  const handleInvoicePay = useCallback((r2Key: string) => {
    setState({ name: 'paying', selfieR2Key: r2Key });
  }, []);

  const handleInvoiceCancel = useCallback(() => {
    clearPersistedR2Key(code);
    navigate('/catalog');
  }, [code, navigate]);

  // Coupon path: redeem a 100%-free code instead of paying with Stars. Rejects
  // (so the invoice sheet shows an inline error) when the code is invalid; on a
  // successful redeem we transition straight into the generation loading state.
  const handleApplyCoupon = useCallback(
    async (r2Key: string, couponCode: string) => {
      const baselineRunId = jwt ? await getBaselineSkuRunId(jwt, code) : null;
      const result = await redeemCoupon(code, couponCode, { selfieR2Key: r2Key });
      let firstRunId = result.runIds?.[0] ?? null;
      if (!firstRunId && jwt) {
        const ids = await discoverSkuRunIds(jwt, code, baselineRunId);
        firstRunId = ids?.runIds[0] ?? null;
      }
      if (!firstRunId) {
        setState({
          name: 'error',
          reason: { kind: 'generation-failed', messageKey: 'pastLives.error.generation' },
        });
        return;
      }
      clearPersistedR2Key(code);
      setActiveRunId(firstRunId);
      setState({ name: 'loading', selfieR2Key: r2Key, runId: firstRunId, startedAt: Date.now() });
    },
    [code, jwt, redeemCoupon],
  );

  // Auto-pay when entering `paying` state.
  useEffect(() => {
    if (state.name !== 'paying') return;
    const r2Key = state.selfieR2Key;
    let cancelled = false;
    (async () => {
      try {
        // Capture the pre-purchase baseline run id so real-Stars discovery can
        // identify the freshly created run without relying on clock agreement.
        const baselineRunId = jwt ? await getBaselineSkuRunId(jwt, code) : null;
        if (cancelled) return;
        const result = await purchase(code, { selfieR2Key: r2Key });
        if (cancelled) return;
        const status = result.status;
        if (status === 'paid') {
          // Mock-pay resolves runIds directly. Real Stars dispatches the run
          // asynchronously in the successful_payment webhook, so the resolved
          // runIds are empty — discover the freshly created run via /sku-runs.
          let firstRunId = result.runIds?.[0] ?? null;
          if (!firstRunId && jwt) {
            const ids = await discoverSkuRunIds(jwt, code, baselineRunId);
            if (cancelled) return;
            firstRunId = ids?.runIds[0] ?? null;
          }
          if (!firstRunId) {
            setState({
              name: 'error',
              reason: { kind: 'generation-failed', messageKey: 'pastLives.error.generation' },
            });
            return;
          }
          clearPersistedR2Key(code);
          setActiveRunId(firstRunId);
          setState({ name: 'loading', selfieR2Key: r2Key, runId: firstRunId, startedAt: Date.now() });
        } else if (status === 'cancelled') {
          setState({
            name: 'error',
            reason: { kind: 'payment-cancelled', messageKey: 'pastLives.error.paymentCancelled' },
          });
        } else {
          setState({
            name: 'error',
            reason: { kind: 'payment-failed', messageKey: 'pastLives.error.payment' },
          });
        }
      } catch {
        if (cancelled) return;
        setState({
          name: 'error',
          reason: { kind: 'payment-failed', messageKey: 'pastLives.error.payment' },
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [state, code, purchase, jwt]);

  // Real generation: flip to reveal once the polled run is READY.
  useEffect(() => {
    if (state.name !== 'loading') return;
    if (!snapshot) return;
    if (snapshot.run.status === 'READY' && snapshot.cards.length > 0) {
      setState({ name: 'reveal-entrance', runId: state.runId, cards: snapshot.cards });
    } else if (snapshot.run.status === 'FAILED') {
      setState({
        name: 'error',
        reason: {
          kind: 'generation-failed',
          messageKey: 'pastLives.error.generation',
          detail: snapshot.run.error ?? undefined,
        },
      });
    } else if (snapshot.run.status === 'REFUNDED') {
      setState({
        name: 'error',
        reason: {
          kind: 'generation-failed',
          messageKey: 'pastLives.error.refunded',
          detail: snapshot.run.error ?? undefined,
        },
      });
    }
  }, [state, snapshot]);

  useEffect(() => {
    if (!runError) return;
    if (state.name !== 'loading') return;
    setState({
      name: 'error',
      reason: { kind: 'network', messageKey: 'pastLives.error.network' },
    });
  }, [runError, state.name]);

  if (isLoading || meLoading) return <div data-testid="past-lives-flow-loading" />;
  if (isError || !sku) {
    return (
      <div data-testid="past-lives-flow-error">
        <p>Couldn't load.</p>
        <button type="button" onClick={() => void navigate('/catalog')}>
          Back to catalog
        </button>
      </div>
    );
  }

  switch (state.name) {
    // ── Freemium ladder ──────────────────────────────────────────────────────
    case 'chart-calc':
      // Poll useChart via the effect above; show a thin loading placeholder.
      return (
        <div data-testid="past-lives-chart-calc" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
          <p style={{ opacity: 0.6 }}>
            {t('pastLives.chartCalc.loading', { defaultValue: 'Составляем натальную карту…' })}
          </p>
        </div>
      );

    case 'free-reveal':
      return (
        <FreeRevealStep
          onTextTier={() => setState({ name: 'upsell' })}
          onPortraitTier={() => setState({ name: 'upsell' })}
          onBack={() => navigate('/catalog')}
        />
      );

    case 'upsell':
      return (
        <UpsellStep
          onTextPurchased={(runId) => navigate(runId ? `/me/result/${runId}` : '/catalog')}
          onPortraitTier={() => {
            // Enter the selfie/portrait flow.
            const persisted = readPersistedR2Key(code);
            if (persisted) {
              setState({ name: 'age-gate', selfieR2Key: persisted });
            } else {
              setState({ name: 'consent' });
            }
          }}
          onBack={() => setState({ name: 'free-reveal' })}
        />
      );

    // ── Existing portrait flow (unchanged) ───────────────────────────────────
    case 'consent':
      return <SelfieConsentModal open jwt={jwt} onAccept={handleConsentAccept} onDecline={handleConsentDecline} />;

    case 'upload':
      return <SelfieUploadScreen skuCode={code} onUploaded={handleUploaded} />;

    case 'age-gate': {
      // While useMe loads, show a thin placeholder so we don't flash a wrong mode.
      if (!ageGateMode) return <div data-testid="past-lives-age-gate-loading" />;
      return (
        <AgeGateModal
          mode={ageGateMode}
          onConfirm={handleAgeGateConfirm}
          onCancel={handleAgeGateCancel}
        />
      );
    }

    case 'invoice': {
      const skuTitle = t(sku.nameKey, { defaultValue: resolveSkuLabel(sku.nameKey) });
      const skuSubtitle = t(sku.descKey, { defaultValue: '' });
      const priceStars = sku.priceStars ?? 0;
      const bullets: string[] = [
        t('pastLives.invoice.bullet.lives', { count: totalCards, defaultValue: '{{count}} past lives, each with portrait and narrative' }),
        t('pastLives.invoice.bullet.hero', { defaultValue: 'One Hero life — the closest karmic echo' }),
        t('pastLives.invoice.bullet.refund', { defaultValue: 'Full refund if anything goes wrong' }),
        t('pastLives.invoice.bullet.private', { defaultValue: 'Yours alone — no public gallery' }),
      ];
      return (
        <PaymentSheet
          skuCode={code}
          title={skuTitle}
          subtitle={skuSubtitle || undefined}
          priceStars={priceStars}
          bullets={bullets}
          glyph={<PastLivesGlyph size={36} />}
          onPay={() => handleInvoicePay(state.selfieR2Key)}
          onCancel={handleInvoiceCancel}
          onApplyCoupon={(couponCode) => handleApplyCoupon(state.selfieR2Key, couponCode)}
        />
      );
    }

    case 'paying':
      return (
        <PastLivesLoadingScreen
          totalCards={totalCards}
          cardsReady={0}
          status={'PENDING'}
          paidStars={paidStars ?? undefined}
        />
      );

    case 'loading':
      return (
        <PastLivesLoadingScreen
          totalCards={totalCards}
          cardsReady={snapshot?.cards.length ?? 0}
          status={snapshot?.run.status ?? 'PENDING'}
          paidStars={paidStars ?? undefined}
        />
      );

    case 'reveal-entrance':
      return (
        <PastLivesRevealEntrance
          cards={state.cards}
          onComplete={() =>
            setState({ name: 'carousel', runId: state.runId, cards: state.cards, index: 0 })
          }
        />
      );

    case 'carousel':
      return (
        <PastLivesCarousel
          cards={state.cards}
          onClose={() => navigate('/catalog')}
          onCrossSell={() => navigate('/sku/avatar_max')}
        />
      );

    case 'error': {
      const reason = state.reason;
      return (
        <div data-testid="past-lives-flow-error-state" role="alert" className="pastLivesFlow-error">
          <h2>
            {t('pastLives.error.title', { defaultValue: 'The chronicles fell silent' })}
          </h2>
          <p>
            {t(reason.messageKey, {
              defaultValue: 'Something interrupted it. Your Stars are safe — try again shortly.',
            })}
          </p>
          {reason.detail && import.meta.env.DEV ? (
            <pre style={{ opacity: 0.5, fontSize: 11, marginTop: 12, whiteSpace: 'pre-wrap' }}>
              {reason.detail}
            </pre>
          ) : null}
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button
              type="button"
              onClick={() => {
                const persisted = readPersistedR2Key(code);
                if (persisted) setState({ name: 'age-gate', selfieR2Key: persisted });
                else setState({ name: 'upload' });
              }}
            >
              {t('pastLives.error.retry', { defaultValue: 'Try again' })}
            </button>
            <button type="button" onClick={() => navigate('/catalog')}>
              {t('pastLives.error.back', { defaultValue: 'Back to catalog' })}
            </button>
          </div>
        </div>
      );
    }

    default:
      return <div />;
  }
}

export default PastLivesFlow;
